import { Loading } from "Shared/Components/Loading/Loading";
import {
	CloudDone,
	CloudOff,
	CloudQueue,
	CloudUpload,
} from "@mui/icons-material";
import {
	Box,
	IconButton,
	type IconButtonProps,
	styled,
	Tooltip,
} from "@mui/material";
import {
	type Control,
	Controller,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";

export enum FormFileTypes {
	html = "text/html",
	pdf = "application/pdf",
	csv = "text/csv",
	jpg = "image/jpeg",
	jpeg = "image/jpeg",
	png = "image/png",
	gif = "image/gif",
	imageAll = "image/*",
}

export const VisuallyHiddenInput = styled("input")({
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	bottom: 0,
	left: 0,
	whiteSpace: "nowrap",
	width: 1,
	zIndex: 3,
});

type FormUploadButtonProps<FormType extends FieldValues> = {
	name: FieldPath<FormType>;
	control: Control<FormType>;
	multiple?: boolean;
	isLoading?: boolean;
	disabled?: boolean;
	valid?: boolean;
	accept?: FormFileTypes;
	customOnChange?: (content: File | FileList) => void;
};

export const FormUploadButton = <FormType extends FieldValues>({
	name,
	control,
	multiple,
	customOnChange,
	isLoading,
	disabled,
	accept,
	valid,
}: FormUploadButtonProps<FormType>) => {
	return (
		<Controller
			name={name}
			control={control}
			render={({ field, fieldState }) => {
				const hasError = !!fieldState.error;
				const errorMessage = fieldState?.error?.message;

				let uploadState = {
					icon: <CloudUpload />,
					color: "primary",
				};

				if (hasError) {
					uploadState = { icon: <CloudOff />, color: "error" };
				} else if (valid) {
					uploadState = { icon: <CloudDone />, color: "success" };
				} else if (valid === false) {
					uploadState = { icon: <CloudQueue />, color: "warning" };
				}

				return (
					<Box tabIndex={-1} sx={{ background: "none !important" }}>
						{isLoading ? (
							<Loading />
						) : (
							<Tooltip title={hasError ? errorMessage : ""} arrow>
								<IconButton
									component="label"
									tabIndex={-1}
									sx={{
										background: "none !important",
										mt: "0 !important",
										p: 0,
									}}
									color={uploadState.color as IconButtonProps["color"]}
								>
									{uploadState.icon}
									<VisuallyHiddenInput
										type="file"
										accept={accept}
										onChange={(event) => {
											const files = event.target.files;
											const value = multiple ? files : files?.[0] || null;

											field.onChange(value);
											value && customOnChange?.(value);
										}}
										multiple={multiple}
										disabled={disabled}
									/>
								</IconButton>
							</Tooltip>
						)}
					</Box>
				);
			}}
		/>
	);
};
